import React, { useEffect, useMemo, useRef, useState } from 'react';

import uniq from 'lodash/uniq';
import { useSearchParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import { Alert, Box, Button, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridFilterOperator,
  GridLogicOperator,
  GridPaginationModel,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';

import { parseDateRange } from '~/components/GHAParticipationReport/reportSearchParams';
import { COUNTRY_OPTIONS, CountryOption, formatCountry } from '~/components/GHAParticipationReport/utils';

import { useGetCompetitorHotelOpportunities } from '~/hooks/apis/price-parity/useGetCompetitorHotelOpportunities';
import { useGetCompetitorProperties } from '~/hooks/apis/price-parity/useGetCompetitorProperties';

import type { Dayjs } from '~/services/TimeService';

import CompetitorOfferGhaOpportunitiesCell from './CompetitorOfferGhaOpportunitiesCell';
import CompetitorOfferLeFlashCell from './CompetitorOfferLeFlashCell';
import CompetitorOfferLppHotelCell from './CompetitorOfferLppHotelCell';
import CompetitorOfferPropertyLink from './CompetitorOfferPropertyLink';
import CompetitorOfferSuppliersPanel from './CompetitorOfferSuppliersPanel';
import CompetitorOfferUserLocationCell from './CompetitorOfferUserLocationCell';
import CompetitorOffersDateRange from './CompetitorOffersDateRange';
import CompetitorOffersNoResults from './CompetitorOffersNoResults';
import CompetitorOffersPresets from './CompetitorOffersPresets';
import CompetitorOffersSearchFilterInput from './CompetitorOffersSearchFilterInput';
import CompetitorOffersToolbar from './CompetitorOffersToolbar';
import {
  COMPETITOR_FLASH_STATUS_LABELS,
  COMPETITOR_OFFERS_PAGE_SIZE,
  CompetitorFlashStatus,
  CompetitorOffersPreset,
  LE_FLASH_STATUS_LABELS,
  LE_HOTEL_MATCH_LABELS,
  LeHotelMatch,
} from './constants';
import {
  CompetitorOfferRow,
  FilterField,
  compareOpportunities,
  competitorFlashStatusOf,
  competitorOfferPath,
  competitorSourceOptions,
  countryCodes,
  flashPropertyVendors,
  formatDate,
  hotelSuppliers,
  leHotelMatchOf,
  matchesFlashIds,
  matchesHotelName,
  matchesPropertyIds,
  matchesUserLocations,
  normaliseIds,
  offerPath,
  opportunitiesFrom,
  pageSearchParams,
  presetOf,
  searchParamsToFilterModel,
  sortByRecentlyCollected,
  sourceLabelOf,
  supplierKey,
  toDateParam,
  unfilteredFields,
  userLocationsOf,
  withPreset,
} from './utils';

// Filter columns, hidden until shown from the Columns panel. Their place in columns sets the filter panel's order.
const HIDDEN_COLUMNS: GridColumnVisibilityModel = {
  sourceName: false,
  onCompetitor: false,
  leHotelMatch: false,
  lePropertyId: false,
  leOfferIds: false,
};

const hotelNameFilterOperators: Array<GridFilterOperator<CompetitorOfferRow>> = [
  {
    label: 'contains',
    value: 'contains',
    getApplyFilterFn: (item) =>
      typeof item.value === 'string' && item.value.trim() ? (_, row) => matchesHotelName(row, item.value) : null,
    InputComponent: CompetitorOffersSearchFilterInput,
  },
];

// Exact ids, several at once.
const idFilterOperators = (
  matches: (property: CompetitorOfferRow, ids: Array<string>) => boolean,
): Array<GridFilterOperator<CompetitorOfferRow>> => [
  {
    label: 'is any of',
    value: 'isAnyOf',
    getApplyFilterFn: (item) => {
      const ids = normaliseIds(item.value);
      return ids.length ? (_, row) => matches(row, ids) : null;
    },
    InputComponent: CompetitorOffersSearchFilterInput,
    InputComponentProps: { multiple: true },
  },
];

// Hotel name and Details are always shown.
const ALWAYS_SHOWN_FIELDS = ['hotelName', 'details'];

const singleSelectOperators = (operator: string) =>
  getGridSingleSelectOperators().filter((candidate) => candidate.value === operator);

// Keeps hotels users searched for from any of the locations; all stay while opportunities load.
const userLocationFilterOperators: Array<GridFilterOperator<CompetitorOfferRow>> = singleSelectOperators('isAnyOf').map(
  (operator) => ({
    ...operator,
    getApplyFilterFn: (item) => {
      const codes = countryCodes(item.value);
      if (!codes.length) {
        return null;
      }
      return (_, row) => matchesUserLocations(row, codes);
    },
  }),
);

const countryColumnOptions: Partial<GridColDef<CompetitorOfferRow>> = {
  type: 'singleSelect',
  valueOptions: COUNTRY_OPTIONS,
  getOptionValue: (option) => (option as CountryOption).code,
  getOptionLabel: (option) => (option as CountryOption).label,
  sortable: false,
};

const columns: Array<GridColDef<CompetitorOfferRow>> = [
  {
    field: 'hotelName',
    headerName: 'Hotel name',
    width: 240,
    valueGetter: (value, row) => value ?? row.externalPropertyId,
    filterOperators: hotelNameFilterOperators,
    renderCell: ({ row, value }) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[sourceLabelOf(row), row.location].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'sourceName',
    headerName: 'Competitor',
    type: 'singleSelect',
    // Set from the loaded hotels.
    valueOptions: [],
    filterOperators: singleSelectOperators('isAnyOf'),
  },
  {
    field: 'onCompetitor',
    headerName: 'Competitor last collection',
    description:
      "Whether the competitor's latest full collection found the hotel. Not found usually means its deal ended, but a collection can miss one",
    type: 'singleSelect',
    valueGetter: (_, row) => competitorFlashStatusOf(row),
    valueOptions: Object.values(CompetitorFlashStatus).map((status) => ({
      value: status,
      label: COMPETITOR_FLASH_STATUS_LABELS[status],
    })),
    filterOperators: singleSelectOperators('is'),
  },
  {
    field: 'onLe',
    headerName: 'LE Flash status',
    type: 'singleSelect',
    width: 190,
    valueGetter: (_, row) => row.leFlashStatus,
    valueOptions: Object.entries(LE_FLASH_STATUS_LABELS).map(([value, label]) => ({ value, label })),
    filterOperators: singleSelectOperators('is'),
    renderCell: ({ row }) => <CompetitorOfferLeFlashCell property={row} />,
  },
  {
    field: 'lppHotel',
    headerName: 'LE LPP hotel',
    description: 'The LPP hotel on Google Hotel Ads matched to this hotel by name and distance',
    width: 240,
    filterable: false,
    valueGetter: (_, row) => row.lppPropertyName ?? row.lppPropertyId,
    renderCell: ({ row }) => <CompetitorOfferLppHotelCell property={row} />,
  },
  {
    ...countryColumnOptions,
    field: 'propertyLocation',
    headerName: 'Property location',
    description: 'The country the hotel is in',
    width: 200,
    // COUNTRY_OPTIONS codes are upper case.
    valueGetter: (_, row) => row.propertyCountryCode?.toUpperCase(),
    filterOperators: singleSelectOperators('isAnyOf'),
    renderCell: ({ value }) => (value ? formatCountry(value) : null),
  },
  {
    field: 'ghaOpportunities',
    headerName: 'Opportunities',
    description:
      "Google Hotel Ads opportunities in the date range: the matched Flash hotel's LPP and LPC listings, else the matched LPP hotel. With a User location filter, only searches from those locations count. N/A when it had no GHA data in the last 100 days",
    type: 'number',
    width: 150,
    filterable: false,
    sortable: true,
    valueGetter: (_, row) =>
      row.gha.status === 'loaded' && row.gha.opportunities
        ? opportunitiesFrom(row.gha.opportunities, row.userLocations)
        : undefined,
    getSortComparator: (direction) => (a, b) => compareOpportunities(a, b, direction ?? 'desc'),
    renderCell: ({ row }) => <CompetitorOfferGhaOpportunitiesCell row={row} />,
  },
  {
    ...countryColumnOptions,
    field: 'userLocation',
    headerName: 'User location',
    description:
      "Where travellers searched Google from, as a share of the hotel's opportunities in the date range; filtered locations come first. Hover for every country",
    width: 240,
    filterOperators: userLocationFilterOperators,
    renderCell: ({ row }) => <CompetitorOfferUserLocationCell row={row} />,
  },
  {
    field: 'leHotelMatch',
    headerName: 'LE hotel match',
    description:
      'Flash when matched to one of our Flash properties, else LPP only when matched to an LPP hotel on Google Hotel Ads',
    type: 'singleSelect',
    valueGetter: (_, row) => leHotelMatchOf(row),
    valueOptions: Object.values(LeHotelMatch).map((match) => ({ value: match, label: LE_HOTEL_MATCH_LABELS[match] })),
    filterOperators: singleSelectOperators('is'),
  },
  {
    field: 'lastCollectedAt',
    headerName: 'Last collected',
    type: 'date',
    width: 190,
    filterable: false,
    valueGetter: (value) => (value ? new Date(value) : null),
    renderCell: ({ row }) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {formatDate(row.lastCollectedAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.lastCollectedAt ? dayjs(row.lastCollectedAt).fromNow() : 'Never collected'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'details',
    headerName: '',
    width: 110,
    filterable: false,
    renderCell: ({ row }) => (
      <Button size="small" variant="outlined" component={Link} to={competitorOfferPath(row)}>
        Details
      </Button>
    ),
  },
  {
    field: 'lePropertyId',
    headerName: 'LE Property ID',
    width: 300,
    valueGetter: (_, row) => row.lePropertyIds.join(' '),
    filterOperators: idFilterOperators(matchesPropertyIds),
    renderCell: ({ row }) => (
      <Stack>
        {[...flashPropertyVendors(row)].map(([propertyId, vendorId]) => (
          <CompetitorOfferPropertyLink key={propertyId} propertyId={propertyId} vendorId={vendorId} />
        ))}
      </Stack>
    ),
  },
  {
    field: 'leOfferIds',
    headerName: 'LE Flash ID',
    width: 200,
    valueGetter: (_, row) => row.leOffers.map((offer) => offer.offerId).join(' '),
    filterOperators: idFilterOperators(matchesFlashIds),
    renderCell: ({ row }) => (
      <Stack>
        {uniq(row.leOffers.map((offer) => offer.offerId)).map((offerId) => (
          <MuiLink
            key={offerId}
            component={Link}
            to={offerPath(offerId)}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            {offerId}
          </MuiLink>
        ))}
      </Stack>
    ),
  },
];

// Only Opportunities sorts; the list otherwise stays latest collected first.
columns.forEach((column) => {
  column.sortable = column.sortable ?? false;
});

const TOGGLABLE_FIELDS = columns.map((column) => column.field).filter((field) => !ALWAYS_SHOWN_FIELDS.includes(field));

export default function CompetitorOffersContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => searchParamsToFilterModel(searchParams));
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => parseDateRange(searchParams));
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: COMPETITOR_OFFERS_PAGE_SIZE,
  });
  // The query string this page last wrote, so only outside URL changes reload the filters.
  const writtenSearch = useRef(searchParams.toString());
  const { data: properties = [], isLoading, error } = useGetCompetitorProperties();
  const opportunities = useGetCompetitorHotelOpportunities(toDateParam(dateRange[0]), toDateParam(dateRange[1]));

  const userLocations = useMemo(() => userLocationsOf(filterModel), [filterModel]);
  const gridColumns = useMemo(() => {
    const valueOptions = competitorSourceOptions(properties);
    return columns.map((column) => (column.field === FilterField.COMPETITOR ? { ...column, valueOptions } : column));
  }, [properties]);

  const rows = useMemo((): Array<CompetitorOfferRow> => {
    const byHotel = new Map(opportunities.data?.map((hotel) => [hotel.hotelKey, hotel]));
    return sortByRecentlyCollected(properties).map((property) => ({
      ...property,
      userLocations,
      gha: opportunities.data
        ? { status: 'loaded', opportunities: property.hotelKey ? byHotel.get(property.hotelKey) : undefined }
        : { status: opportunities.isError ? 'error' : 'loading' },
    }));
  }, [properties, opportunities.data, opportunities.isError, userLocations]);

  useEffect(() => {
    const search = searchParams.toString();
    if (search !== writtenSearch.current) {
      writtenSearch.current = search;
      setFilterModel(searchParamsToFilterModel(searchParams));
      setDateRange(parseDateRange(searchParams));
    }
  }, [searchParams]);

  const writeSearchParams = (nextFilterModel: GridFilterModel, nextDateRange: [Dayjs, Dayjs]) => {
    const nextSearchParams = pageSearchParams(nextFilterModel, nextDateRange);
    if (nextSearchParams.toString() !== writtenSearch.current) {
      writtenSearch.current = nextSearchParams.toString();
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  const handleFilterModelChange = (nextFilterModel: GridFilterModel) => {
    setFilterModel(nextFilterModel);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    writeSearchParams(nextFilterModel, dateRange);
  };

  const handleDateRangeChange = (nextDateRange: [Dayjs, Dayjs]) => {
    setDateRange(nextDateRange);
    writeSearchParams(filterModel, nextDateRange);
  };

  const handlePresetChange = (preset: CompetitorOffersPreset) =>
    handleFilterModelChange(withPreset(filterModel, preset));

  const handleCustom = () => {
    if (presetOf(filterModel)) {
      handleFilterModelChange(withPreset(filterModel, undefined));
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Hotels collected from competitor sites, matched to our flash properties and LPP hotels on Google Hotel Ads. User
        location is where travellers searched Google from; property location is where the hotel is. Prices are in the
        competitor&apos;s currency, with AUD at the rate of the day they were collected. Collected once a day; room
        prices and terms are not rechecked against the live site.
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap gap={2}>
        <CompetitorOffersPresets filterModel={filterModel} onChange={handlePresetChange} onCustom={handleCustom} />
        <CompetitorOffersDateRange value={dateRange} onChange={handleDateRangeChange} />
      </Stack>

      <Box mt={2}>
        {error && <Alert severity="error">Failed to load competitor offers: {error.message}</Alert>}
        {!error && opportunities.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn&apos;t load Google Hotel Ads opportunities: {opportunities.error.message}
            {userLocations.length > 0 &&
              '. The User location filter can only leave out hotels without a match until they load.'}
          </Alert>
        )}
        {!error && (
          <Paper variant="outlined">
            <DataGridPro
              autoHeight
              rows={rows}
              columns={gridColumns}
              getRowId={(row: CompetitorOfferRow) => supplierKey(row)}
              loading={isLoading}
              getRowHeight={() => 'auto'}
              initialState={{ columns: { columnVisibilityModel: HIDDEN_COLUMNS } }}
              disableColumnMenu
              sortingOrder={['desc', 'asc', null]}
              disableRowSelectionOnClick
              pagination
              pageSizeOptions={[COMPETITOR_OFFERS_PAGE_SIZE]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              getDetailPanelContent={({ row }) => (
                <CompetitorOfferSuppliersPanel suppliers={hotelSuppliers(properties, row)} />
              )}
              getDetailPanelHeight={() => 'auto'}
              slots={{ toolbar: CompetitorOffersToolbar, noResultsOverlay: CompetitorOffersNoResults }}
              slotProps={{
                columnsManagement: { getTogglableColumns: () => TOGGLABLE_FIELDS },
                filterPanel: {
                  logicOperators: [GridLogicOperator.And],
                  // One filter per column: "is any of" covers several values, and a second AND filter could only narrow it.
                  getColumnForNewFilter: ({ currentFilters }) => unfilteredFields(currentFilters)[0] ?? null,
                  filterFormProps: {
                    filterColumns: ({ field, currentFilters }) => [field, ...unfilteredFields(currentFilters)],
                    // AND is the only option, so the disabled "And" select is noise.
                    logicOperatorInputProps: { sx: { display: 'none' } },
                    columnInputProps: { sx: { width: 240 } },
                    valueInputProps: { sx: { width: 420 }, InputComponentProps: { limitTags: 3 } },
                  },
                },
              }}
              sx={{ border: 0, '& .MuiDataGrid-cell': { py: 1.5, display: 'flex', alignItems: 'center' } }}
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
}
